/**
 * Agent-runner domain shapes (DEVOP-22/24, ADR 0062–0065). A `AgentRun` is one
 * prompt-to-completion task the browser-automation agent executes; `AgentRunEvent`
 * is the per-step log trail (the audit record ADR 0065 §4 relies on);
 * `AgentArtifact` is a pointer to a report/recording/screenshot produced by a run
 * (the actual bytes live in R2 / Cloudflare Browser Run — this is metadata only).
 *
 * Zod here per ADR 0005 ("parse, don't validate"): these are persisted-on-read and
 * cross the service boundary (agent-runner-api ↔ the future agent-runner web app),
 * so both sides must parse the SAME shapes.
 */
import { z } from 'zod';

// `awaiting_input` (ADR 0078) — a conversational session that finished a turn and is alive,
// waiting for the operator's next message. Distinct from `completed` (session ended) and
// `awaiting_human` (a mid-turn handoff pause). A one-shot run never enters it.
export const AGENT_RUN_STATUSES = ['queued', 'running', 'awaiting_human', 'awaiting_input', 'completed', 'failed', 'cancelled'] as const;
export const AgentRunStatusSchema = z.enum(AGENT_RUN_STATUSES);
export type AgentRunStatus = z.infer<typeof AgentRunStatusSchema>;

/**
 * How a run actually executes (DEVOP-49 / ADR 0106 §1).
 *
 * `browser` — a Container browser session, watched over Live View, logging a step per action.
 * `warehouse-flight` — a MotherDuck Flight: no container, no Live View, and **no step log at all**;
 *   its progress crosses the Event Hub as plan/milestone facts instead (ADR 0104 §2).
 *
 * A **closed enum**, not free text, and that is the point: the console renders a run by switching on
 * this, so an unknown kind has to be a *visible, labelled* state rather than an unrenderable one. A
 * new execution model should be a reviewed contract change, not a string invented at a call site.
 */
export const AGENT_RUN_KINDS = ['browser', 'warehouse-flight'] as const;
export const AgentRunKindSchema = z.enum(AGENT_RUN_KINDS);
export type AgentRunKind = z.infer<typeof AgentRunKindSchema>;

export const AgentRunSchema = z.object({
  id: z.string().min(1),
  ownerId: z.string().min(1),
  prompt: z.string().min(1).max(4000),
  status: AgentRunStatusSchema,
  createdAt: z.number().int().nonnegative(),
  startedAt: z.number().int().nonnegative().nullable(),
  completedAt: z.number().int().nonnegative().nullable(),
  /** Set only when `status` is `failed` — a human-readable, non-sensitive cause. */
  errorMessage: z.string().max(2000).nullable(),
  /**
   * What this run IS. Defaulted to `browser` because every row written before ADR 0106 is one —
   * the field states what the shape used to merely imply.
   */
  kind: AgentRunKindSchema.default('browser'),
  /**
   * Which catalogued agent owns it (`packages/contracts/src/catalogue/agents.json`). Nullable
   * because a run can outlive a catalogue row, or predate the catalogue entirely — the console shows
   * such a run as unregistered rather than hiding it (ADR 0106 §4).
   */
  agentId: z.string().min(1).max(120).nullable().default(null),
  /**
   * The Hub event that asked for this run, and the thread every fact about it shares (DEVOP-49).
   *
   * Null is **meaningful, not missing**: a browser run is not on the bus at all — nothing publishes
   * an event to start one and it emits no Hub facts, so its record is its step log. A UI must
   * render that as "not event-driven", never as an empty event list.
   */
  sourceEventId: z.string().min(1).max(120).nullable().default(null),
  correlationId: z.string().min(1).max(120).nullable().default(null),
});
export type AgentRun = z.infer<typeof AgentRunSchema>;

/**
 * `step` — one agent action (browser-use's `on_step_start`/`on_step_end`).
 * `human_requested` — the run paused for a captcha/login (ADR 0065 §3).
 * `human_resolved` — a human completed the handoff; the run resumed.
 * `error` — a non-fatal error surfaced mid-run (the run may still continue).
 * `message` — a follow-up the operator sent into a live session (ADR 0078); the user's turn.
 * `turn_completed` — one turn of a session finished, carrying that turn's summary/report (ADR 0078).
 * `completed` — the terminal event carrying the final summary (the session/run ended).
 */
export const AGENT_RUN_EVENT_KINDS = ['step', 'human_requested', 'human_resolved', 'error', 'message', 'turn_completed', 'completed'] as const;
export const AgentRunEventKindSchema = z.enum(AGENT_RUN_EVENT_KINDS);
export type AgentRunEventKind = z.infer<typeof AgentRunEventKindSchema>;

export const AgentRunEventSchema = z.object({
  id: z.string().min(1),
  runId: z.string().min(1),
  /** Monotonic per-run sequence number — the ordering key (timestamps alone can tie). */
  seq: z.number().int().nonnegative(),
  kind: AgentRunEventKindSchema,
  /** Structural — shape varies per `kind` (a step's action/thought, a human-requested reason, …). */
  payload: z.record(z.unknown()),
  occurredAt: z.number().int().nonnegative(),
});
export type AgentRunEvent = z.infer<typeof AgentRunEventSchema>;

export const AGENT_ARTIFACT_KINDS = ['report', 'recording', 'screenshot', 'live_view'] as const;
export const AgentArtifactKindSchema = z.enum(AGENT_ARTIFACT_KINDS);
export type AgentArtifactKind = z.infer<typeof AgentArtifactKindSchema>;

export const AgentArtifactSchema = z.object({
  id: z.string().min(1),
  runId: z.string().min(1),
  kind: AgentArtifactKindSchema,
  /** Pointer to the artifact (an R2 key, a Browser Run recording URL, or — for
   *  `live_view` — the run's live Browser Run view URL, handed out only via the
   *  Access-gated `/live-view` route, never returned in bulk artifact lists to a browser). */
  url: z.string().min(1),
  createdAt: z.number().int().nonnegative(),
});
export type AgentArtifact = z.infer<typeof AgentArtifactSchema>;

/**
 * `AgentFinding` — one raw "fact" the agent surfaced during a run (a page's
 * extracted content, the final structured result, a form-submission
 * confirmation, …). The **landing-zone** shape (DEVOP-25 §3.4): a stable
 * envelope with a loose `payload`, so a downstream pipeline dispatches on
 * `kind` + `schemaVersion` without this table migrating every time the agent
 * extracts a new kind of thing. Written during/after a run, owner-scoped.
 */
export const AgentFindingSchema = z.object({
  id: z.string().min(1),
  runId: z.string().min(1),
  ownerId: z.string().min(1),
  /** e.g. 'final_result' | 'page_extract' | 'form_result' — free-form by design (landing zone). */
  kind: z.string().min(1).max(200),
  /** Where the fact came from, when applicable (the page URL); null for run-level facts. */
  sourceUrl: z.string().max(4000).nullable(),
  /** Shape depends on `kind` — deliberately loose here; the pipeline parses it downstream. */
  payload: z.record(z.unknown()),
  schemaVersion: z.number().int().positive(),
  capturedAt: z.number().int().nonnegative(),
});
export type AgentFinding = z.infer<typeof AgentFindingSchema>;

/**
 * `AgentSiteSession` — METADATA about a saved post-login session (ADR 0069). The
 * encrypted `storage_state` ciphertext is deliberately NOT a field here: it never
 * leaves the internal container↔api seam, and this metadata shape is what a future
 * "your saved sessions" browser view (backlog D7) would list. Scoped per site.
 */
export const AgentSiteSessionSchema = z.object({
  ownerId: z.string().min(1),
  domain: z.string().min(1),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
});
export type AgentSiteSession = z.infer<typeof AgentSiteSessionSchema>;
